import React, { useState } from "react";
import { SECTOR_WEIGHTS, POINTS_PER_RANK, MIN_VOLUME_GATE, type SectorFilter } from "./constants";

// =============================================================================
// FORMULA GUIDE — Standing "How this is calculated" panel
// =============================================================================
// Mounted below the pill-row controls on LeaderboardPage. Closed by default.
// Sector-aware: shows weights matching the active tab.
//
// CSS rules observed:
// - liquid-glass-light (16px radius, 8px blur)
// - text-glass / text-glass-secondary / text-glass-muted
// - NO transform on hover states
// - NO bg-white/N (light mode only)
// =============================================================================

const WeightBlocks: React.FC<{
  weights: { label: string; pct: number; desc: string }[];
}> = ({ weights }) => (
  <div className="flex gap-2 mt-2">
    {weights.map((w) => (
      <div
        key={w.label}
        className="flex-1 rounded-md p-2 bg-black/[0.03] border border-black/[0.06] text-center"
      >
        <p className="token-15 text-glass">{w.pct}%</p>
        <p className="token-13 text-glass-secondary mt-0.5">{w.label}</p>
        <p className="token-11 text-glass-muted">{w.desc}</p>
      </div>
    ))}
  </div>
);

const SECTOR_BLEND_LABEL: Record<SectorFilter, string> = {
  closer: "rank_score = 50% × Cash_norm + 25% × ShowRate_norm + 25% × CPC_norm",
  setter: "rank_score = 40% × CPC_norm + 30% × ShowRate_norm + 30% × Booked_norm",
  dialer: "rank_score = 30% × CPC_norm + 25% × Dials_norm + 25% × CTB_norm + 20% × ShowRate_norm",
};

interface FormulaGuideProps {
  sector: SectorFilter;
}

const FormulaGuide: React.FC<FormulaGuideProps> = ({ sector }) => {
  const [isOpen, setIsOpen] = useState(false);
  const gate = MIN_VOLUME_GATE[sector];
  const weights = SECTOR_WEIGHTS[sector];

  return (
    <div className="liquid-glass-light anim-lift rounded-[16px] overflow-visible">
      {/* Header row — always visible */}
      <button
        onClick={() => setIsOpen((v) => !v)}
        className="w-full flex items-center justify-between px-4 py-3 hover:bg-black/[0.02] transition-colors text-left rounded-[16px]"
        aria-expanded={isOpen}
      >
        <span className="token-13 font-semibold text-glass">How this is calculated</span>
        <svg
          className={`w-4 h-4 text-glass-muted transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {/* Collapsible body */}
      {isOpen && (
        <div className="px-4 pb-4 space-y-4 border-t border-black/[0.06]">
          {/* 1. Daily Prestige Points */}
          <div className="pt-3">
            <p className="token-15 text-glass font-semibold">Daily Prestige Points</p>
            <p className="token-13 text-glass-secondary mt-1">
              Each day, ranked reps earn points based on their daily rank:
            </p>
            <div className="flex gap-2 mt-2">
              {POINTS_PER_RANK.map((p) => (
                <div
                  key={p.rank}
                  className="flex-1 rounded-md p-2 bg-black/[0.03] border border-black/[0.06] text-center"
                >
                  <p className="token-13 font-semibold text-glass">{p.pts} pts</p>
                  <p className="token-11 text-glass-muted mt-0.5">{p.rank}</p>
                </div>
              ))}
            </div>
          </div>

          {/* 2. Rank Score Formula */}
          <div>
            <p className="token-15 text-glass font-semibold">Rank Score Formula</p>
            <p className="token-13 text-glass-muted mt-1 font-mono break-words">
              {SECTOR_BLEND_LABEL[sector]}
            </p>
            <p className="token-13 text-glass-secondary mt-1">
              Each metric is normalized 0→1 against the top performer that day. Weights:
            </p>
            <WeightBlocks weights={weights} />
          </div>

          {/* 3. Min-Volume Gate */}
          <div>
            <p className="token-15 text-glass font-semibold">Min-Volume Gate</p>
            <p className="token-13 text-glass-secondary mt-1">
              To be ranked, you need <span className="font-semibold text-glass">{gate.label}</span>{" "}
              in the period. Below the gate = unranked, no points. This keeps a 1-call rep from
              dominating the score pool.
            </p>
          </div>

          {/* 4. Monthly Prestige Rollup */}
          <div>
            <p className="token-15 text-glass font-semibold">Monthly Prestige Rollup</p>
            <p className="token-13 text-glass-secondary mt-1">
              Daily points sum across the month. Top 3 in each pod earn prestige rank-ups + cash
              bonuses on Day 1 of next month.
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default FormulaGuide;
